import { FC, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Box } from "~/components/Box";
import { Container } from "~/components/Container";
import { ProfileMenu } from "../ProfileMenu";
import useMobile from "~/presentation/shared/hooks/useMobile";
import { SideNavMenu } from "../NavMenu/SideNavMenu";
import { BiztripLogo } from "~/components/BiztripLogo";
import { useRemakeSearch } from "~/core/shared/contexts/remakeSearchContext";
import { NavMenu } from "../NavMenu";
import { Styled } from "./styled";
import { useTheme } from "~/containers/DynamicThemeProvider";
import { colors as themeTokens } from "~/application/theme/colors";
import { getNavBarPosition } from "../../utils";

export const NavBar: FC = () => {
  const navigate = useNavigate();
  const { data, currentStep } = useRemakeSearch();
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const isMobile = useMobile();
  const { colors } = useTheme();

  const menuNavigate = (route: string) => {
    navigate(route);
    setMenuIsOpen(false);
  };

  const path = useLocation().pathname;

  const isMobileAbsolute = getNavBarPosition(path, true) === "absolute";
  const isDesktopAbsolute = getNavBarPosition(path, false) === "absolute";

  const navBackgroundColor = isMobileAbsolute ? "transparent" : themeTokens["primary-100"];
  const desktopBackgroundColor = isDesktopAbsolute
    ? "transparent"
    : colors?.["header-background"] || "white";

  return (
    <Styled.Container
      data-nav-bar
      css={{
        backgroundColor: desktopBackgroundColor,
        "@mxlg": { position: "relative", p: "$5", backgroundColor: navBackgroundColor },
        "@mxmd": { position: "relative", p: "$4", backgroundColor: navBackgroundColor },
        "@mxsm": { position: "relative", p: "$3", backgroundColor: navBackgroundColor },
        py: "0",
      }}
      as={Container}
    >
      {isMobile && menuIsOpen && (
        <SideNavMenu onNavigate={menuNavigate} onCloseMenu={() => setMenuIsOpen(false)} />
      )}

      <Box
        css={{
          "@initial": { flexBasis: "20%" },
          "@mxlg": { flexBasis: "25%" },
          "@mxmd": { flexBasis: "30%" },
          "@mxsm": { flexBasis: "35%" },
        }}
      >
        <BiztripLogo
          size={{
            "@initial": "lg",
            "@mxlg": "md",
            "@mxmd": "md",
            "@mxsm": "sm",
          }}
          css={{
            ["& .bt-biz-paths"]: {
              fill: colors["primary-base"],
            },
            ["& .bt-trip-paths"]: {
              fill: colors["primary-base"],
            },
          }}
          variant={{
            "@initial": "dark",
            "@mxlg": "mobile",
            "@mxmd": "mobile",
            "@mxsm": "mobile",
          }}
          data-cy="biztrip-logo"
          onClick={() => {
            if (data.road && currentStep !== data.road.length) return;
            navigate("/");
          }}
        />
      </Box>

      <Box
        css={{
          "@mxlg": {
            display: "none",
          },
        }}
      >
        <NavMenu />
      </Box>

      <ProfileMenu />
    </Styled.Container>
  );
};

NavBar.displayName = "NavBar";
